type BarcodeDetectorFormat = 'qr_code';

type DetectedBarcode = {
  rawValue?: string;
};

export type BarcodeDetectorInstance = {
  detect: (source: ImageBitmapSource) => Promise<DetectedBarcode[]>;
};

type BarcodeDetectorConstructor = {
  new (options?: {
    formats?: BarcodeDetectorFormat[];
  }): BarcodeDetectorInstance;
  getSupportedFormats?: () => Promise<BarcodeDetectorFormat[]>;
};

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructor;
  }
}

const getBarcodeDetector = (): BarcodeDetectorConstructor | null => {
  if (typeof window === 'undefined') return null;
  return window.BarcodeDetector ?? null;
};

const createJsQrDetector =
  async (): Promise<BarcodeDetectorInstance | null> => {
    try {
      const jsQR = (await import('jsqr')).default;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;

      return {
        detect: async (
          source: ImageBitmapSource,
        ): Promise<DetectedBarcode[]> => {
          if (!(source instanceof HTMLVideoElement)) return [];
          const { videoWidth, videoHeight } = source;
          if (!videoWidth || !videoHeight) return [];
          canvas.width = videoWidth;
          canvas.height = videoHeight;
          ctx.drawImage(source, 0, 0, videoWidth, videoHeight);
          const imageData = ctx.getImageData(0, 0, videoWidth, videoHeight);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          return code ? [{ rawValue: code.data }] : [];
        },
      };
    } catch {
      return null;
    }
  };

let cachedDetector: BarcodeDetectorInstance;

const createQrCodeDetector = async (): Promise<BarcodeDetectorInstance> => {
  const BarcodeDetectorApi = getBarcodeDetector();
  if (BarcodeDetectorApi) {
    const supportedFormats =
      (await BarcodeDetectorApi.getSupportedFormats?.()) ?? [];
    if (supportedFormats.length && !supportedFormats.includes('qr_code')) {
      throw new Error(
        'This browser camera does not support QR scanning. Enter the value manually.',
      );
    }
    return new BarcodeDetectorApi({ formats: ['qr_code'] });
  }

  const jsQrDetector = await createJsQrDetector();
  if (!jsQrDetector) {
    throw new Error(
      'QR scanning is not supported in this browser. Enter the value manually.',
    );
  }
  return jsQrDetector;
};

export async function getQrCodeDetector(): Promise<BarcodeDetectorInstance> {
  cachedDetector ??= await createQrCodeDetector();
  return cachedDetector;
}
